/* eslint-disable react/jsx-curly-spacing */
import { Alert, Button, FocusTrap } from 'crux';
import React, { useState, useEffect } from 'react';
import styled, { withTheme } from 'styled-components';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { postMakeCardpointePayment } from '../../util/externalServices';
import { updateAnswersArray } from '../../redux/actions/questions';
import Cover from '../StyledComponents/MaskCover/Index';
import {
  CloseButton,
  ModalBody,
  ModalHeading,
  ModalWrapper,
  QuestionDescription,
} from '../UIElements/Modal/ModalStyles';
import CardpointeIFrame from './CardpointeIFrame';
import { getParams } from '../../util';
import { isCustomRole } from '../../utils';

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const errorEvent = {
  message: '',
  expiry: '',
  validationError: 'default error',
};

dayjs.extend(customParseFormat);

// modal component
const CardpointePaymentModal = ({
  handleClose,
  theme,
  response,
  questionObj,
  sectionIndex,
  handleCloseToggleParentQuestion,
}) => {
  const [tokenEvent, setTokenEvent] = useState(errorEvent);
  const [isLoading, setIsLoading] = useState(false);
  const [alertDetails, setAlertDetails] = useState({});
  const {
    config: {
      theme: {
        components: {
          card: { themeConfiguration: cardThemeConfiguration },
        },
      },
    },
  } = theme;

  const colorOverride = get(theme, 'config.theme.global.colorOverride', null);
  const modalButtonTheme = get(theme, 'config.theme.global.modalButton.themeConfiguration', {});
  const commonBtnTheme = get(theme, 'config.theme.global.commonButtonTheme');

  const themeConfiguration = colorOverride
    ? {
        selectedBgColor: colorOverride.selectedBgColor,
        bgColor: '#ffffff',
        border: `1px solid ${colorOverride.selectedBgColor}`,
        fontColor: '#333333',
      }
    : { ...modalButtonTheme, ...commonBtnTheme };

  useEffect(() => {
    document.body.style.overflow = response?.message === 'Approval' ? 'auto' : 'hidden';

    if (response) {
      handleClose({ response, questionObj, sectionIndex, preventReflexiveCall: true });
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleCloseClick = () => {
    handleCloseToggleParentQuestion({ sectionIndex });
  };

  const handleMakePayment = async () => {
    const token = tokenEvent.message;
    const expiryDate = dayjs(tokenEvent.expiry, 'YYYYMM');
    const formattedExpiryDate = `${expiryDate.year()}${expiryDate.month() + 1}`;

    const payload = {
      amount: '100.65',
      expiry: formattedExpiryDate,
      uid: getParams('uid'),
      token,
    };
    setIsLoading(true);
    const result = await postMakeCardpointePayment({ payload });
    if (result) {
      const { errors, data } = result;

      if (errors || data.payment_status === 'F') {
        const alertData = {
          type: 'error',
          message: 'Payment cannot be processed at this time. Please try again later.',
          title: 'Please try again.',
        };
        setAlertDetails({ ...alertData, show: true });
        setIsLoading(false);
        return;
      }

      let alertData = {};

      if (data.payment_status === 'A') {
        alertData = { type: 'info', message: 'Payment Successful.', title: 'Success' };
        setAlertDetails({ ...alertData, show: true });
        setTimeout(() => {
          handleClose({ response: data, questionObj, sectionIndex });
          setIsLoading(false);
        }, 2500);
      } else {
        const message = isCustomRole(getParams('role'))
          ? 'Credit Card declined.'
          : 'Your credit card information could not be verified. Please try again.';
        alertData = { type: 'error', message, title: 'Please try again.' };
        setAlertDetails({ ...alertData, show: true });
        setIsLoading(false);
      }
    }
  };

  const handlePayment = (latestTokenEvent) => {
    if (latestTokenEvent && latestTokenEvent.message === '' && latestTokenEvent.expiry === '') {
      setAlertDetails({ show: false });
    }
    setTokenEvent(latestTokenEvent);
  };

  if (response) return null;

  return (
    <FocusTrap>
      <Cover name='cover' />
      <ModalWrapper name='modal-wrapper' cardThemeConfiguration={cardThemeConfiguration}>
        {isLoading ? <Cover /> : null}
        <CloseButton onClick={handleCloseClick}>Close</CloseButton>
        {alertDetails.show && (
          <Alert
            message={alertDetails.message}
            type={alertDetails.type}
            title={alertDetails.title}
            hasCloseIcon={false}
          />
        )}
        <ModalHeading name='modal-heading'>Initial Premium Payment</ModalHeading>
        <QuestionDescription error={false}>make payment description</QuestionDescription>
        <ModalBody name='modal-body'>
          <CardpointeIFrame
            handlePayment={handlePayment}
            useexpiry='true'
            usecvv='true'
            cardlabel='Enter Card Number'
            expirylabel='Enter Expiry Date'
            cvvlabel='Enter CVV'
            cardnumbernumericonly='true'
            invalidcreditcardevent='true'
            invalidexpiryevent='true'
            invalidcvvevent='true'
            maskfirsttwo='true'
            formatinput='true'
          />
          <ButtonWrapper name='button-wrapper'>
            <Button
              name='confirm-button'
              label='Make Payment'
              type='filled'
              handleButtonClick={handleMakePayment}
              width='90%'
              disabled={!!tokenEvent.validationError || alertDetails.show}
              themeConfiguration={themeConfiguration}
              alignment='center'
              tabIndex='0'
            />
          </ButtonWrapper>
        </ModalBody>
      </ModalWrapper>
    </FocusTrap>
  );
};

const mapStateToProps = (state) => {
  const paymentModalDetails = get(state, 'questions.paymentModalDetails', {});
  return {
    paymentModalDetails,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleCloseToggleParentQuestion: ({ sectionIndex }) => {
      // creditcard checkbox question should be unchecked when user cancles the payment
      dispatch(
        updateAnswersArray({
          questionId: '581ea3aa-d36b-4add-8273-daa4885c5d4c',
          response: 'ed957ef0-05a6-4f36-b597-49a5fdd020ff',
          sectionIndex,
          isControlledInput: true,
          isHidden: false,
        }),
      );
    },
    handleClose: ({ response, questionObj, sectionIndex, preventReflexiveCall = false }) => {
      dispatch(
        updateAnswersArray({
          questionId: questionObj.question_id,
          response,
          sectionIndex,
          isControlledInput: true,
          preventReflexiveCall,
          isHidden: false,
        }),
      );
    },
  };
};

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(CardpointePaymentModal));
